import { useEffect, useMemo, useState } from 'react';
import Modal from '../common/Modal';
import { BTN_PRIMARY, BTN_SECONDARY, INPUT, LABEL } from '../common/ui';
import type { DiscoveredCamera } from '../../api';

/** Token in the RTSP templates that the backend swaps for each camera's real IP. */
export const IP_TOKEN = '<IP>';

interface PathPreset {
  key: string;
  label: string;
  /** Templates with the IP_TOKEN placeholder. */
  main: string;
  sub: string;
}

/**
 * Manufacturer-aware RTSP path presets. Templates carry the `<IP>` token; the
 * backend substitutes each camera's real IP at approval time. The user can edit
 * the generated URLs before confirming.
 */
const PRESETS: PathPreset[] = [
  { key: 'hikvision', label: 'Hikvision', main: `rtsp://${IP_TOKEN}:554/Streaming/Channels/101`, sub: `rtsp://${IP_TOKEN}:554/Streaming/Channels/102` },
  { key: 'dahua', label: 'Dahua', main: `rtsp://${IP_TOKEN}:554/cam/realmonitor?channel=1&subtype=0`, sub: `rtsp://${IP_TOKEN}:554/cam/realmonitor?channel=1&subtype=1` },
  { key: 'axis', label: 'Axis', main: `rtsp://${IP_TOKEN}:554/axis-media/media.amp?resolution=1920x1080`, sub: `rtsp://${IP_TOKEN}:554/axis-media/media.amp?resolution=640x480` },
  { key: 'generic', label: 'Generic', main: `rtsp://${IP_TOKEN}:554/stream1`, sub: `rtsp://${IP_TOKEN}:554/stream2` },
];

/** Map a manufacturer string to the closest preset key (defaults to generic). */
function presetKeyForManufacturer(manufacturer?: string): string {
  const m = (manufacturer || '').toLowerCase();
  if (m.includes('hikvision') || m.includes('hik')) return 'hikvision';
  if (m.includes('dahua')) return 'dahua';
  if (m.includes('axis')) return 'axis';
  return 'generic';
}

/**
 * Embed URL-encoded credentials into the authority of an `rtsp://…` template so
 * the stored URL carries the auth, e.g.
 *   rtsp://<IP>:554/cam/realmonitor?channel=1&subtype=0
 *   → rtsp://admin:India123%23@<IP>:554/cam/realmonitor?channel=1&subtype=0
 * Uses encodeURIComponent (matches the ingest pipeline's Python `quote(safe='')`),
 * so `#` → %23, `@` → %40, etc. The `<IP>` token is left intact for the backend.
 */
function withCredentials(template: string, username: string, password: string): string {
  if (!username) return template;
  const userInfo = password
    ? `${encodeURIComponent(username)}:${encodeURIComponent(password)}`
    : encodeURIComponent(username);
  return template.replace(/^rtsp:\/\//i, `rtsp://${userInfo}@`);
}

/** Pick the preset matching the most common manufacturer among the selection. */
function defaultPresetKey(cameras: DiscoveredCamera[]): string {
  const counts = new Map<string, number>();
  for (const c of cameras) {
    const key = presetKeyForManufacturer(c.manufacturer);
    counts.set(key, (counts.get(key) || 0) + 1);
  }
  let best = 'generic';
  let bestN = -1;
  for (const [key, n] of counts) {
    if (n > bestN) { best = key; bestN = n; }
  }
  return best;
}

export interface BulkAddCredentials {
  rtsp_url: string;
  sub_stream_rtsp_url: string;
  rtsp_username: string;
  rtsp_password: string;
}

export interface BulkAddCredentialsModalProps {
  open: boolean;
  /** Cameras selected for bulk add (used for the IP summary and default preset). */
  cameras: DiscoveredCamera[];
  loading?: boolean;
  error?: string;
  onCancel: () => void;
  onConfirm: (creds: BulkAddCredentials) => void;
}

/**
 * Bulk add-camera credentials modal. The operator enters one username/password and
 * a shared main/sub RTSP template (auto-filled per manufacturer, `<IP>` placeholder);
 * the same template is applied to every selected camera, with the backend swapping
 * `<IP>` for each camera's real IP on store.
 */
export default function BulkAddCredentialsModal({
  open, cameras, loading = false, error, onCancel, onConfirm,
}: BulkAddCredentialsModalProps) {
  const [presetKey, setPresetKey] = useState('generic');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [mainUrl, setMainUrl] = useState('');
  const [subUrl, setSubUrl] = useState('');
  // Once the user edits a URL, stop overwriting it when the preset changes.
  const [urlsTouched, setUrlsTouched] = useState(false);

  // Initialise the preset when the modal opens for a new selection; the
  // regeneration effect below fills the URL fields from preset + credentials.
  useEffect(() => {
    if (!open) return;
    setPresetKey(defaultPresetKey(cameras));
    setUrlsTouched(false);
    setUsername('');
    setPassword('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Regenerate the main/sub URLs from the selected preset with the entered
  // credentials embedded (URL-encoded). Stops once the user edits a URL by hand.
  useEffect(() => {
    if (!open || urlsTouched) return;
    const preset = PRESETS.find((p) => p.key === presetKey) || PRESETS[3];
    setMainUrl(withCredentials(preset.main, username.trim(), password));
    setSubUrl(withCredentials(preset.sub, username.trim(), password));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, presetKey, username, password, urlsTouched]);

  // Switching vendor regenerates the URLs with the current credentials.
  function applyPreset(key: string) {
    setPresetKey(key);
    setUrlsTouched(false);
  }

  const ips = useMemo(() => cameras.map((c) => c.ip_address), [cameras]);

  if (!open) return null;

  const inputClass = INPUT;

  function handleConfirm() {
    if (!mainUrl.trim()) return;
    onConfirm({
      rtsp_url: mainUrl.trim(),
      sub_stream_rtsp_url: subUrl.trim(),
      rtsp_username: username.trim(),
      rtsp_password: password,
    });
  }

  return (
    <Modal
      open={open}
      // Escape / backdrop / close are ignored while the cameras are being added.
      onClose={() => { if (!loading) onCancel(); }}
      title={`Add ${cameras.length} camera${cameras.length !== 1 ? 's' : ''}`}
      size="md"
      footer={(
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onCancel} disabled={loading} className={BTN_SECONDARY}>
            Cancel
          </button>
          <button type="button" onClick={handleConfirm} disabled={loading || !mainUrl.trim()} className={BTN_PRIMARY}>
            {loading ? 'Adding…' : `Add ${cameras.length} camera${cameras.length !== 1 ? 's' : ''}`}
          </button>
        </div>
      )}
    >
        {error && (
          <div className="mx-6 mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-[13px] text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
            {error}
          </div>
        )}

        <div className="px-6 py-5 space-y-4">
          <p className="text-[12px] text-muted-foreground">
            Enter credentials and an RTSP template once — it's applied to every selected camera.
            The username/password are embedded into the URL (encoded), and the
            <code className="rounded bg-muted px-1 font-mono text-[11px]">{IP_TOKEN}</code> placeholder is replaced with each camera's IP.
          </p>

          {/* Credentials */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={LABEL}>Username</label>
              <input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="admin" className={inputClass} />
            </div>
            <div>
              <label className={LABEL}>Password</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className={inputClass} />
            </div>
          </div>

          {/* Manufacturer preset */}
          <div>
            <label className={LABEL}>RTSP URL format</label>
            <div className="flex flex-wrap gap-2">
              {PRESETS.map((p) => (
                <button key={p.key} type="button" onClick={() => applyPreset(p.key)}
                  className={`rounded-lg border px-3 py-1.5 text-[12px] font-medium transition-colors ${
                    presetKey === p.key ? 'border-brand bg-brand/10 text-brand' : 'border-border text-muted-foreground hover:border-accent hover:bg-muted'
                  }`}>{p.label}</button>
              ))}
            </div>
          </div>

          {/* Main / Sub templates */}
          <div>
            <label className={LABEL}>Main RTSP URL *</label>
            <input value={mainUrl} onChange={(e) => { setMainUrl(e.target.value); setUrlsTouched(true); }}
              placeholder={`rtsp://${IP_TOKEN}:554/stream1`} className={`${inputClass} font-mono`} />
          </div>
          <div>
            <label className={LABEL}>Sub Stream RTSP URL</label>
            <input value={subUrl} onChange={(e) => { setSubUrl(e.target.value); setUrlsTouched(true); }}
              placeholder={`rtsp://${IP_TOKEN}:554/stream2 (optional)`} className={`${inputClass} font-mono`} />
          </div>

          {/* Selected IPs summary */}
          <div>
            <label className="mb-1 block text-[12px] font-medium text-muted-foreground">Applies to {ips.length} IP{ips.length !== 1 ? 's' : ''}</label>
            <div className="max-h-24 overflow-y-auto rounded-lg border border-border bg-muted/30 px-3 py-2 text-[11px] font-mono text-muted-foreground">
              {ips.join(', ')}
            </div>
          </div>
        </div>

    </Modal>
  );
}
